'use client';

import React, { useCallback, useEffect, useState } from 'react';
import 'owl.carousel/dist/assets/owl.carousel.css';
import 'owl.carousel/dist/assets/owl.theme.default.css';
import classNames from 'classnames';

import useEmblaCarousel from 'embla-carousel-react';
import { containerWidthOptions } from '../../editor/Toolbar/options';
import { resolveSetting } from '../../../utils/settingsHelper';
import { NextButton, PrevButton, usePrevNextButtons } from './CarouselArrows';
import { getTranslation } from './carouselTranslations';

const FADE_AUTOPLAY_INTERVAL = 4000;

export const getSlideClass = (cols) => {
	switch (cols) {
	case '2':
		return 'embla__2col';
	case '3':
		return 'embla__3col';
	case '4':
		return 'embla__4col';
	case '5':
		return 'embla__5col';
	case '6':
		return 'embla__6col';
	case '1':
	default:
		return 'embla__1col';
	}
};

export const getSlideMobileClass = (cols) => {
	switch (cols) {
	case '2':
		return 'embla__mobile_2col';
	case '3':
		return 'embla__mobile_3col';
	case '4':
		return 'embla__mobile_4col';
	case '5':
		return 'embla__mobile_5col';
	case '6':
		return 'embla__mobile_6col';
	case '1':
	default:
		return 'embla__mobile_1col';
	}
};

export function DotButton({ selected, onClick, index, language = 'en', activeColor }) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-label={getTranslation(language, 'goToSlide', { slideNumber: index + 1 })}
			aria-current={selected ? 'true' : 'false'}
			style={selected && activeColor ? { backgroundColor: activeColor } : undefined}
			className={classNames('rounded-full w-2 h-2 mr-2', selected ? (!activeColor && 'bg-blue-700') : 'bg-gray-300')}
		/>
	);
}

export function MeltingDotButton({ selected, onClick, index, language = 'en', activeColor }) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-label={getTranslation(language, 'goToSlide', { slideNumber: index + 1 })}
			aria-current={selected ? 'true' : 'false'}
			style={selected && activeColor ? { backgroundColor: activeColor } : undefined}
			className={classNames(
				'rounded-full mr-2 transition-all duration-300',
				selected ? `w-5 h-2 ${!activeColor ? 'bg-blue-700' : ''}` : 'w-2 h-2 bg-gray-300',
			)}
		/>
	);
}

export function useFadeCarousel(slideCount, { autoplay, loop } = {}) {
	const [activeIndex, setActiveIndex] = useState(0);

	const clamp = useCallback((index) => {
		if (slideCount <= 0) return 0;
		if (loop) return (index + slideCount) % slideCount;
		return Math.min(Math.max(index, 0), slideCount - 1);
	}, [slideCount, loop]);

	useEffect(() => {
		setActiveIndex((current) => clamp(current));
	}, [clamp]);

	const goTo = useCallback((index) => setActiveIndex(clamp(index)), [clamp]);
	const onNextButtonClick = useCallback(() => setActiveIndex((current) => clamp(current + 1)), [clamp]);
	const onPrevButtonClick = useCallback(() => setActiveIndex((current) => clamp(current - 1)), [clamp]);

	useEffect(() => {
		if (!autoplay || slideCount <= 1) return undefined;
		const id = setInterval(() => {
			setActiveIndex((current) => {
				if (!loop && current >= slideCount - 1) return current;
				return clamp(current + 1);
			});
		}, FADE_AUTOPLAY_INTERVAL);
		return () => clearInterval(id);
	}, [autoplay, loop, slideCount, clamp]);

	return {
		activeIndex,
		goTo,
		onNextButtonClick,
		onPrevButtonClick,
		prevBtnDisabled: !loop && activeIndex === 0,
		nextBtnDisabled: !loop && activeIndex >= slideCount - 1,
	};
}

export function FadeSlides({ children, activeIndex }) {
	const items = React.Children.toArray(children);

	return (
		<div className="grid">
			{items.map((child, index) => (
				<div
					key={child.key ?? index}
					aria-hidden={index === activeIndex ? undefined : 'true'}
					className={classNames(
						'embla__slide col-start-1 row-start-1 transition-opacity duration-700 ease-in-out',
						index === activeIndex ? 'relative z-10 opacity-100' : 'opacity-0 pointer-events-none',
					)}
				>
					{child}
				</div>
			))}
		</div>
	);
}

export function LeftNavigation({ hasArrows, onPrevButtonClick, onNextButtonClick, prevBtnDisabled, nextBtnDisabled, slides, selectedIndex, onDotClick, language = 'en', activeColor }) {
	return (
		<div className="w-full flex justify-start items-center gap-2 my-2 text-center">
			{hasArrows && <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} language={language} />}
			{hasArrows && <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} language={language} />}
			<div className="flex items-center">
				{slides.map((_, index) => (
					<MeltingDotButton
						key={index}
						index={index}
						selected={index === selectedIndex}
						onClick={() => onDotClick(index)}
						language={language}
						activeColor={activeColor}
					/>
				))}
			</div>
		</div>
	);
}

export default function Carousel({ children, maxWidth, cols, mobileCols, hasArrows, autoplay, autoplayMode, loop, navigationStyle = 'default', transitionEffect = 'slide', pageLanguage: language = 'en', dotActiveColor, settings }) {
	const isFade = transitionEffect === 'fade';
	const isOverlayNav = navigationStyle === 'overlay';
	const slideCount = React.Children.count(children);
	const activeColor = resolveSetting(dotActiveColor, settings);

	const [plugins, setPlugins] = useState([]);
	const [emblaSlides, setEmblaSlides] = useState([]);
	const [emblaRef, embla] = useEmblaCarousel({ slidesToScroll: 'auto', loop: !!loop }, plugins);

	useEffect(() => {
		let cancelled = false;
		async function loadPlugins() {
			if (!autoplay || isFade) { setPlugins([]); return; }
			const mod = autoplayMode === 'continuous'
				? await import('embla-carousel-auto-scroll')
				: await import('embla-carousel-autoplay');
			if (!cancelled) setPlugins([mod.default({ stopOnInteraction: true })]);
		}
		loadPlugins();
		return () => { cancelled = true; };
	}, [autoplay, autoplayMode, isFade]);
	const [emblaSelectedIndex, setEmblaSelectedIndex] = useState(0);

	const {
		prevBtnDisabled: emblaPrevDisabled,
		nextBtnDisabled: emblaNextDisabled,
		onPrevButtonClick: onEmblaPrevButtonClick,
		onNextButtonClick: onEmblaNextButtonClick,
	} = usePrevNextButtons(embla);

	const onSelect = useCallback(() => {
		if (!embla) return;
		setEmblaSelectedIndex(embla.selectedScrollSnap());
	}, [embla, setEmblaSelectedIndex]);

	useEffect(() => {
		if (!embla) return;
		onSelect();
		setEmblaSlides(embla.scrollSnapList());
		embla.on('select', onSelect);
	}, [embla, setEmblaSlides, onSelect, children, cols]);

	const emblaScrollTo = useCallback((index) => embla && embla.scrollTo(index), [embla]);

	const {
		activeIndex: fadeActiveIndex,
		goTo: fadeGoTo,
		onPrevButtonClick: onFadePrevButtonClick,
		onNextButtonClick: onFadeNextButtonClick,
		prevBtnDisabled: fadePrevDisabled,
		nextBtnDisabled: fadeNextDisabled,
	} = useFadeCarousel(slideCount, { autoplay, loop });

	const selectedIndex = isFade ? fadeActiveIndex : emblaSelectedIndex;
	const slides = isFade ? React.Children.toArray(children) : emblaSlides;
	const scrollTo = isFade ? fadeGoTo : emblaScrollTo;
	const onPrevButtonClick = isFade ? onFadePrevButtonClick : onEmblaPrevButtonClick;
	const onNextButtonClick = isFade ? onFadeNextButtonClick : onEmblaNextButtonClick;
	const prevBtnDisabled = isFade ? fadePrevDisabled : emblaPrevDisabled;
	const nextBtnDisabled = isFade ? fadeNextDisabled : emblaNextDisabled;

	return (
		<div
			className={classNames('w-full overflow-hidden @sm:mx-auto')}
			// style={{ maxWidth: containerWidthOptions[maxWidth] }}
		>
			<div className="embla" style={{ maxWidth: containerWidthOptions[maxWidth] }}>
				{isFade ? (
					<FadeSlides activeIndex={fadeActiveIndex}>
						{children}
					</FadeSlides>
				) : (
					<div className="overflow-hidden" ref={emblaRef}>
						<ul className={classNames('embla__container', getSlideClass(cols), getSlideMobileClass(mobileCols))}>
							{children}
						</ul>
					</div>
				)}
				{isOverlayNav ? (
					<LeftNavigation
						hasArrows={hasArrows}
						onPrevButtonClick={onPrevButtonClick}
						onNextButtonClick={onNextButtonClick}
						prevBtnDisabled={prevBtnDisabled}
						nextBtnDisabled={nextBtnDisabled}
						slides={slides}
						selectedIndex={selectedIndex}
						onDotClick={scrollTo}
						language={language}
						activeColor={activeColor}
					/>
				) : (
					<div className="w-full flex justify-between my-2 text-center">
						{hasArrows && <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} language={language} />}
						<div className="mx-auto">
							{(isFade || !loop) && slides.map((_, index) => (
								<DotButton
									key={index}
									index={index}
									selected={index === selectedIndex}
									onClick={() => scrollTo(index)}
									language={language}
									activeColor={activeColor}
								/>
							))}
						</div>
						{hasArrows && <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} language={language} />}
					</div>
				)}
			</div>
		</div>
	);
}
